import { format, isValid, parse } from 'date-fns';
import React from 'react';
import ReactDatePicker from 'react-datepicker';
import { useHasPermission } from '../hooks/useHasPermission';
import { InlineEdit } from './inline-edit';
import 'react-datepicker/dist/react-datepicker.css';

type InlineDateEditProps = {
  permission: string;
  value: Date | null;
  onChange: (value: Date | null) => void;
  placeholder?: string;
  displayFormat?: string;
  small?: boolean;
  disabled?: boolean;
  className?: string;
  dateFormat?: string;
  onBlur?: (value: Date | null) => void;
  [key: string]: unknown; // Allow other ReactDatePicker props
};

export function InlineDateEdit({
  permission,
  value,
  onChange,
  onBlur,
  placeholder = 'Select date',
  displayFormat = 'MM/dd/yyyy',
  small = false,
  disabled = false,
  className,
  dateFormat,
  ...datePickerProps
}: InlineDateEditProps) {
  const canEdit = useHasPermission(permission);

  const handleDisplay = (date: Date | null) => {
    if (date) {
      return format(date, displayFormat);
    }
    return placeholder;
  };

  const datePickerClassName = small
    ? 'datepicker-input datepicker-input-small'
    : 'datepicker-input';
  const wrapperClassName = small
    ? 'datepicker-wrapper tw:!mt-0 tw:!w-36 tw:before:hidden tw:after:hidden'
    : 'datepicker-wrapper tw:!w-36 tw:before:hidden tw:after:hidden';

  const { onBlur: userOnBlur, ...pickerRest } = datePickerProps as {
    onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
    [key: string]: unknown;
  };

  return (
    <InlineEdit<Date | null>
      readOnly={!canEdit}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      renderDisplay={handleDisplay}
      renderEditor={(date, handleChange, onBlurCommit) => (
        <ReactDatePicker
          {...pickerRest}
          className={datePickerClassName}
          wrapperClassName={wrapperClassName}
          selected={date}
          onChange={(newDate) => {
            // Always sync picker state. Some interactions (manual typing / programmatic input)
            // invoke onChange with a falsy `event` argument; ignoring those leaves InlineEdit stale.
            handleChange(newDate);
          }}
          onSelect={(selectedDate) => {
            handleChange(selectedDate);
            setTimeout(() => {
              onBlurCommit(selectedDate);
            }, 0);
          }}
          onBlur={(e) => {
            userOnBlur?.(e);
            // react-datepicker (and Playwright fill) may leave the raw input out of sync with
            // controlled `selected`; document-capture mousedown can flush before blur. Parse the
            // input value so InlineEdit's ref matches the visible field before committing.
            const el = e.target;
            if (el instanceof HTMLInputElement) {
              const raw = el.value.trim();
              if (raw) {
                const parsePattern = typeof dateFormat === 'string' ? dateFormat : displayFormat;
                const parsed = parse(raw, parsePattern, new Date());
                if (isValid(parsed)) {
                  handleChange(parsed);
                }
              }
            }
            onBlurCommit();
          }}
          placeholderText={placeholder}
          disabled={disabled}
          dateFormat={dateFormat}
        />
      )}
      small={small}
      className={className}
    />
  );
}
